import { WindowServiceInterface } from '@propertyfinder/pf-frontend-common/src/service/window/service.interface';
import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';

import { PropertyModel } from 'common/data/property/model';
import { configGetSite } from 'common/helper/config/get-site';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { DataStore } from 'common/module/data/store';
import { FormPropertyShareView } from 'common/module/form/property-share/view';
import { PropertySocialShareViewInterface } from 'common/module/property/social-share/view.interface';
import { PropertyPageSocialShareTemplatePropsInterface } from 'common/module/property-page-social-share/template-props.interface';
import { PropertyPageSocialShareViewOptionsInterface } from 'common/module/property-page-social-share/view-options.interface';
import { StatsServiceInterface } from 'common/service/stats/service.interface';

export class PropertyPageSocialShareViewStore extends DataStore {
  /**
   * @inheritDoc
   */
  protected state: PropertyPageSocialShareTemplatePropsInterface;

  /**
   * Property page social share strategy
   */
  protected strategy: PropertySocialShareViewInterface;

  /**
   * @inheritDoc
   */
  constructor(
    eventEmitter: EventEmitterInterface,
    private statsService: StatsServiceInterface,
    private propertyShareFormView: FormPropertyShareView,
    private windowService: WindowServiceInterface
  ) {
    super(eventEmitter);
  }

  /**
   * @inheritDoc
   */
  public initialize(options: PropertyPageSocialShareViewOptionsInterface): void {
    this.setState({
      model: options.model,
      onClickShareFacebook: this.onClickShareFacebook,
      onClickSharePinterest: this.onClickSharePinterest,
      onClickShareWhatsapp: this.onClickShareWhatsapp,
      onClickShareTwitter: this.onClickShareTwitter,
      onClickShareEmail: this.onClickShareEmail,
      onClickShareWithFriend: this.onClickShareWithFriend,
      onClickClose: this.onClickClose,
      getPropertyShareLinkForUtmPlatform: this.getPropertyShareLinkForUtmPlatform,
    });
  }

  /**
   * Set strategy
   */
  public setStrategy(strategy: PropertySocialShareViewInterface): void {
    this.strategy = strategy;
  }

  /**
   * Set share data
   */
  public setData(propertyUrl: string, defaultImage: string, isDesktop: boolean): void {
    this.setState({
      ...this.getState(),
      propertyUrl,
      defaultImage,
      isDesktop,
    });
  }

  /**
   * @inheritDoc
   */
  public getState(): PropertyPageSocialShareTemplatePropsInterface {
    return this.state;
  }

  /**
   * Show property share by email form
   */
  public showPropertyShareByEmailForm(): void {
    if (this.strategy) {
      this.strategy.destroy();
    }
    this.propertyShareFormView.initialize();
    this.propertyShareFormView.updateModel(this.state.model);
    this.propertyShareFormView.render();
  }

  /**
   * @description Opens share modal
   * @param property
   */
  public showShare(property: PropertyModel): void {
    if (this.windowService.getNative()?.navigator?.share) {
      this.windowService.getNative().navigator.share(this.getShareData(property));
    } else {
      this.getState().onClickShareWithFriend();
    }
  }

  /**
   * @description Returns data to be shared via the Navigator.share api
   * @param property
   * @returns {ShareData}
   */
  private getShareData(property: PropertyModel): ShareData {
    return {
      text: i18nTranslate(
        'Hi, I found this home on {sitename}. Please check it out and tell me what you think...'
      ).replace('{sitename}', configGetSite().name),
      title: property.name,
      url: property.share_url,
    };
  }

  /**
   * On click facebook share, log stats
   */
  private onClickShareFacebook = (): void => {
    this.statsService.ctaClick({
      cta: {
        customEvent: {
          label: 'Social Sharing',
          action: 'Click:Facebook Share',
          name: 'User Interaction',
        },
      },
    });
  };

  /**
   * On click pinterest share, log stats
   */
  private onClickSharePinterest = (): void => {
    this.statsService.ctaClick({
      cta: {
        customEvent: {
          label: 'Social Sharing',
          action: 'Click:Pinterest Share',
          name: 'User Interaction',
        },
      },
    });
  };

  /**
   * On click whatsapp share, log stats
   */
  private onClickShareWhatsapp = (): void => {
    this.statsService.ctaClick({
      cta: {
        customEvent: {
          label: 'Social Sharing',
          action: 'Click:Whatsapp Share',
          name: 'User Interaction',
        },
      },
    });
  };

  /**
   * On click twitter share, log stats
   */
  private onClickShareTwitter = (): void => {
    this.statsService.ctaClick({
      cta: {
        customEvent: {
          label: 'Social Sharing',
          action: 'Click:Twitter Share',
          name: 'User Interaction',
        },
      },
    });
  };

  /**
   * On click email share, show share by email form
   */
  private onClickShareEmail = (): void => {
    this.showPropertyShareByEmailForm();
  };

  /**
   * On click share with friend button
   */
  private onClickShareWithFriend = (): void => {
    if (this.strategy) {
      this.strategy.initialize();
      this.strategy.render();
    } else {
      this.showPropertyShareByEmailForm();
    }
  };

  /**
   * On click close button
   */
  private onClickClose = (): void => {
    this.strategy.close();
  };

  /**
   * Property social share link
   */
  private getPropertyShareLinkForUtmPlatform = (platform: string): string => {
    return (
      this.state.propertyUrl +
      encodeURIComponent(`?utm_source=${platform}&utm_medium=social&utm_campaign=share_property`)
    );
  };
}
